import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { profileTrips } from '../../data/profileTrips';
import {
  Activity,
  CreateActivityRequest,
  Trip,
  createTrip,
  createActivity,
  deleteTrip,
  deleteActivity,
  getActivities,
  getTripById,
  setActivityTravelMode,
  updateActivity,
  updateTrip,
} from '../../../services/trips/api/tripsApi';
import type { CreateTripRequest, TripDates, ActivityTravelLeg, TravelMode } from '../../../services/trips/api/tripsApi';
import { formatBudget, normalizeStatus } from '../../../services/trips/utils/tripUtils';
import { formatTripDates, parseLocalDate, toNaiveDateTime } from '../../../services/trips/utils/dateUtils';
import { extractActivityUuid } from '../../../services/trips/utils/idUtils';
import { getTokensFromCache } from '../../../services/auth/utils/tokenCache';
import { useWebSocketContext } from '../../../services/websocket/context/WebSocketContext';
import ActionRequestCard from '../../components/chat/ActionRequestCard';
import ReactionBar from '../../components/social/ReactionBar';
import { Reaction, tripReaction } from '../../../services/engagement/api';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { ActionRequest, ActionResolution, collectFallbackActivities } from '../../../services/trips/actionRequests';
import { WebSocketMessage } from '../../../services/websocket/hooks/useWebSocket';
import TripMapSidebar from '../../components/maps/TripMapSidebar';
import {
  LocationRecommendation,
  normalizeLocationRecommendations,
} from '../../components/maps/googlePlacesService';
import FormattedChatMessage from '../../components/chat/FormattedChatMessage';
import './TripDetailPage.css';

const trip = {
  id: 'portugal',
  title: 'Portugal Escape',
  status: 'PLANNED',
  dateRange: 'Oct 12 – 17',
  duration: '5 days',
  stops: ['Lisbon', 'Sintra', 'Porto'],
  budget: '$1,500',
  travelers: 2,
  imageUrl: 'https://images.unsplash.com/photo-1513735492246-483525079686?auto=format&fit=crop&w=300&q=80',
  description: 'This trip in Portugal is food first and nature second, with coastal views, historic hills, and a relaxed route across Lisbon, Sintra, and Porto.',
};

interface TripActivity {
  id: string;
  day: string;
  description: string;
  startTime: string;
  endTime: string;
  place?: string;
  budget?: string;
  category: 'travel' | 'activity' | 'food';
  travelFromPrev?: ActivityTravelLeg; // inbound travel leg (T1.16)
}

type TripActivityDraft = Omit<TripActivity, 'id' | 'day'>;

interface AgentResponse {
  intent: string;
  confidence: number;
  message: string;
  trip_draft?: Record<string, any>;
  locations_recommendations?: Array<Record<string, any>>;
  activities?: Array<Record<string, any>>;
  action_request?: ActionRequest;
}

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  isError?: boolean;
  intent?: string;
  trip_draft?: Record<string, any>;
  locations_recommendations?: LocationRecommendation[];
  activities?: Array<Record<string, any>>;
  action_request?: ActionRequest;
  actionResolution?: ActionResolution;
  createdTripId?: string;
  actionResolutionNote?: string;
}

interface TripBasicsForm {
  title: string;
  destinations: string[];
  startDate: string;
  endDate: string;
  budget: string;
  budgetCurrency: string;
  adults: string;
  children: string;
}

const tripDays = ['MON 12', 'TUE 13', 'WED 14', 'THU 15', 'FRI 16'];
const userOwnedTripIds = new Set([
  'iceland',
  'portugal',
  'japan',
  'alps',
  'amsterdam',
  'barcelona',
  'sedona',
  'draft',
]);

const publicTripPhotos = [
  'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=700&q=80',
  'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=700&q=80',
];

const budgetBreakdown = [
  { label: 'Airplane tickets', value: '$520' },
  { label: 'Hotels & stays', value: '$420' },
  { label: 'Food', value: '$260' },
  { label: 'Events', value: '$160' },
  { label: 'Places & transport', value: '$140' },
];

const mapBackendTripToDetail = (backendTrip: Trip, canEditTrip: boolean) => {
  const destinations = backendTrip.destinations?.length ? backendTrip.destinations : ['Trip route'];
  const budget = formatBudget(backendTrip.budget);
  const imageUrl = backendTrip.images?.find((image) => /^https?:\/\//.test(image)) || trip.imageUrl;

  return {
    ...trip,
    id: String(backendTrip.id),
    title: backendTrip.title,
    status: canEditTrip ? getDetailStatusLabel(backendTrip.status) : 'PUBLIC TRIP',
    dateRange: formatTripDates(backendTrip.dates),
    duration: getDetailTripDuration(backendTrip),
    stops: destinations,
    budget: budget || trip.budget,
    travelers: formatTravelers(backendTrip.travelers) || String(trip.travelers),
    imageUrl,
    description: backendTrip.description || trip.description,
  };
};

const getDetailStatusLabel = (status: string) => {
  const normalized = normalizeStatus(status);

  if (normalized === 'in_progress') return 'IN PROGRESS';
  if (normalized === 'completed') return 'COMPLETED';
  if (normalized === 'draft') return 'DRAFT';
  if (normalized === 'confirmed') return 'CONFIRMED';
  return 'PLANNED';
};

const getDetailTripDuration = (backendTrip: Trip) => {
  if (!backendTrip.dates?.startDate || !backendTrip.dates?.endDate) return trip.duration;

  const start = parseLocalDate(backendTrip.dates.startDate);
  const end = parseLocalDate(backendTrip.dates.endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return trip.duration;
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};

const emptyDraftBasics: TripBasicsForm = {
  title: '',
  destinations: [''],
  startDate: '',
  endDate: '',
  budget: '',
  budgetCurrency: 'USD',
  adults: '',
  children: '',
};

const buildTripBasicsForm = (backendTrip: Trip | null): TripBasicsForm => ({
  title: backendTrip?.title || '',
  destinations: backendTrip?.destinations?.length ? backendTrip.destinations : [''],
  startDate: backendTrip?.dates?.startDate || '',
  endDate: backendTrip?.dates?.endDate || '',
  budget: getBudgetAmount(backendTrip?.budget),
  budgetCurrency: getBudgetCurrency(backendTrip?.budget),
  adults: String(backendTrip?.travelers?.adults ?? 1),
  children: String(backendTrip?.travelers?.children ?? 0),
});

const formatDraftBudget = (basics: TripBasicsForm): string => {
  if (!basics.budget) return '—';

  const amount = Number(basics.budget);
  if (!Number.isFinite(amount) || amount <= 0) return '—';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: basics.budgetCurrency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${basics.budgetCurrency || 'USD'} ${amount}`;
  }
};

const formatDraftDuration = (startDate: string, endDate: string): string => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return '';
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};

const formatDraftDateSummary = (basics: TripBasicsForm): string => {
  if (!basics.startDate && !basics.endDate) return 'Dates not set';

  if (basics.startDate && basics.endDate) {
    const dates = formatTripDates(buildTripDates(basics.startDate, basics.endDate));
    const duration = formatDraftDuration(basics.startDate, basics.endDate);
    return duration ? `${dates} · ${duration}` : dates;
  }

  if (basics.startDate) {
    return `Starts ${formatTripDates(buildTripDates(basics.startDate, basics.startDate))}`;
  }

  return `Ends ${formatTripDates(buildTripDates(basics.endDate, basics.endDate))}`;
};

const formatDraftTravelers = (basics: TripBasicsForm): string => {
  if (!basics.adults && !basics.children) return '—';

  const adults = Number(basics.adults) || 0;
  const children = Number(basics.children) || 0;
  const total = adults + children;
  return total > 0 ? String(total) : '—';
};

const buildDisplayedTripFromBasics = (basics: TripBasicsForm, description: string) => {
  const stops = basics.destinations.map((destination) => destination.trim()).filter(Boolean);

  return {
    id: 'draft',
    title: basics.title.trim() || 'Untitled trip',
    status: 'DRAFT',
    dateRange: formatDraftDateSummary(basics),
    duration: '',
    stops,
    budget: formatDraftBudget(basics),
    travelers: formatDraftTravelers(basics),
    imageUrl: '',
    description: description.trim(),
  };
};

const formatTravelers = (travelers?: { adults: number; children: number }) => {
  if (!travelers) return null;
  const parts: string[] = [];
  if (travelers.adults) parts.push(`${travelers.adults} adult${travelers.adults === 1 ? '' : 's'}`);
  if (travelers.children) parts.push(`${travelers.children} child${travelers.children === 1 ? '' : 'ren'}`);
  return parts.join(', ') || '1 adult';
};

const getBudgetAmount = (budget: Trip['budget']) => {
  if (!budget) return '';
  if (typeof budget === 'object') return String(budget.amount || '');
  return String(budget);
};

const getBudgetCurrency = (budget: Trip['budget']) => {
  if (budget && typeof budget === 'object') return budget.currency || 'USD';
  return 'USD';
};

const buildTripDates = (startDate: string, endDate: string): TripDates => ({
  type: 'concrete',
  startDate,
  endDate,
});

const parseAgentResponse = (text: string): { response: AgentResponse | null; isError: boolean } => {
  if (!text || typeof text !== 'string') {
    return { response: null, isError: false };
  }

  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return { response: null, isError: false };
  }

  try {
    const parsed = JSON.parse(trimmed) as any;
    const response = parsed.response?.intent ? parsed.response : parsed.intent ? parsed : null;

    if (response?.intent && response.message !== undefined && response.confidence !== undefined) {
      return { response, isError: false };
    }
  } catch {
    return { response: null, isError: false };
  }

  return { response: null, isError: false };
};

const mapServerActivityToTripActivity = (activity: Activity, fallbackDay: string): TripActivity => {
  const date = activity.dateTime ? new Date(activity.dateTime) : null;
  const endDate = activity.endDateTime ? new Date(activity.endDateTime) : null;
  const startTime = date && !Number.isNaN(date.getTime())
    ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '09:00';
  // Prefer the activity's real end time; fall back to +1h from the start.
  const endReference = endDate && !Number.isNaN(endDate.getTime())
    ? endDate
    : (date && !Number.isNaN(date.getTime()) ? new Date(date.getTime() + 60 * 60 * 1000) : null);
  const endTime = endReference
    ? endReference.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '10:00';

  return {
    id: activity.id,
    day: date && !Number.isNaN(date.getTime()) ? formatActivityDay(date) : fallbackDay,
    description: activity.title,
    startTime,
    endTime,
    place: activity.location,
    budget: formatBudget(activity.budget) || undefined,
    category: inferActivityCategory(activity.title, activity.location),
    travelFromPrev: activity.travelFromPrev,
  };
};

const inferActivityCategory = (title: string, location: string): TripActivity['category'] => {
  const text = `${title} ${location}`.toLowerCase();

  if (/(flight|train|bus|drive|taxi|transfer|airport)/.test(text)) return 'travel';
  if (/(dinner|lunch|breakfast|food|restaurant|cafe|coffee)/.test(text)) return 'food';
  return 'activity';
};

const formatActivityDay = (date: Date) =>
  `${date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} ${date.getDate()}`;

const formatDayHeading = (dayLabel: string, tripStartDate?: string): string => {
  if (!tripStartDate) {
    const [weekday, dayNumber] = dayLabel.split(' ');
    return `${weekday} ${dayNumber}`;
  }

  const start = parseLocalDate(tripStartDate);
  if (Number.isNaN(start.getTime())) {
    return dayLabel;
  }

  const cursor = new Date(start);
  for (let index = 0; index < 21; index += 1) {
    if (formatActivityDay(cursor) === dayLabel) {
      return cursor.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return dayLabel;
};

const getTripDayLabels = (backendTrip: Trip | null, activities: TripActivity[]) => {
  if (backendTrip?.dates?.startDate && backendTrip?.dates?.endDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    const end = parseLocalDate(backendTrip.dates.endDate);
    const labels: string[] = [];

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const cursor = new Date(start);
      while (cursor <= end && labels.length < 21) {
        labels.push(formatActivityDay(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    if (labels.length > 0) return labels;
  }

  const activityDays = Array.from(new Set(activities.map((activity) => activity.day)));
  if (activityDays.length > 0) return activityDays;

  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    if (!Number.isNaN(start.getTime())) {
      return [formatActivityDay(start)];
    }
  }

  return [];
};

const getDefaultTripDay = (backendTrip: Trip | null) => {
  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    if (!Number.isNaN(start.getTime())) {
      return formatActivityDay(start);
    }
  }

  return '';
};

const buildActivityDateTime = (day: string, time: string, backendTrip: Trip | null) => {
  const [weekday, dayNumber] = day.split(' ');

  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    const targetDay = Number(dayNumber);
    const cursor = new Date(start);

    for (let index = 0; index < 21; index += 1) {
      if (
        cursor.getDate() === targetDay &&
        cursor.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase() === weekday
      ) {
        const [hours, minutes] = time.split(':').map(Number);
        // Naive wall-clock (no toISOString UTC shift) — same format as the
        // agent/chat path so both write paths agree and render identically.
        return toNaiveDateTime(cursor, hours || 0, minutes || 0);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  const fallback = new Date();
  const [hours, minutes] = time.split(':').map(Number);
  return toNaiveDateTime(fallback, hours || 0, minutes || 0);
};

const TripDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tripId } = useParams<{ tripId: string }>();
  const webSocket = useWebSocketContext();
  const isDraft = tripId === 'draft';
  const routeState = location.state as { backTo?: string; owned?: boolean; trip?: Trip } | null;
  const backTo = routeState?.backTo;
  const [serverTrip, setServerTrip] = React.useState<Trip | null>(routeState?.trip || null);
  const isKnownPublicProfileTrip = profileTrips.some((item) => item.id === tripId);
  const canEditTrip = Boolean(
    isDraft
    || routeState?.owned
    || (tripId && userOwnedTripIds.has(tripId))
    || (serverTrip && tripId && !isKnownPublicProfileTrip),
  );
  const [isLoadingTrip, setIsLoadingTrip] = React.useState(!isDraft && !routeState?.trip);
  const [tripError, setTripError] = React.useState<string | null>(null);
  const [isLoadingActivities, setIsLoadingActivities] = React.useState(false);
  const [activityError, setActivityError] = React.useState<string | null>(null);
  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
  const [selectedRecommendation, setSelectedRecommendation] = React.useState<LocationRecommendation | null>(null);
  const [currentInput, setCurrentInput] = React.useState('');
  const [isLoadingConversation, setIsLoadingConversation] = React.useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = React.useState(false);
  const [conversationId, setConversationId] = React.useState(routeState?.trip?.conversation_id || '');
  const chatMessagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const profileTrip = profileTrips.find((item) => item.id === tripId);
  const [activities, setActivities] = React.useState<TripActivity[]>([]);
  const [tripDescription, setTripDescription] = React.useState('');
  const [tripBasics, setTripBasics] = React.useState<TripBasicsForm>(() =>
    isDraft ? emptyDraftBasics : buildTripBasicsForm(routeState?.trip || null),
  );
  const displayedTrip = React.useMemo(() => {
    if (serverTrip) {
      return mapBackendTripToDetail(serverTrip, canEditTrip);
    }

    if (isDraft) {
      return buildDisplayedTripFromBasics(tripBasics, tripDescription);
    }

    if (profileTrip) {
      return {
        ...trip,
        id: profileTrip.id,
        title: profileTrip.title,
        status: canEditTrip ? trip.status : 'PUBLIC TRIP',
        dateRange: profileTrip.meta.split(' · ')[0] || trip.dateRange,
        duration: profileTrip.meta.split(' · ')[1] || trip.duration,
        imageUrl: profileTrip.imageUrl,
        description: profileTrip.description,
      };
    }

    return trip;
  }, [serverTrip, canEditTrip, isDraft, tripBasics, tripDescription, profileTrip]);
  const [tripBasicsError, setTripBasicsError] = React.useState<string | null>(null);
  const [isSavingTripBasics, setIsSavingTripBasics] = React.useState(false);
  const [editingActivityId, setEditingActivityId] = React.useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = React.useState<string | null>(null);
  const tripDayLabels = React.useMemo(() => getTripDayLabels(serverTrip, activities), [serverTrip, activities]);
  const [selectedDay, setSelectedDay] = React.useState('');
  const [activityDraft, setActivityDraft] = React.useState<TripActivityDraft>(createEmptyActivityDraft());
  const [isActivityModalOpen, setIsActivityModalOpen] = React.useState(false);
  // Edit-basics modal for an already-created trip (drafts edit inline instead).
  const [isEditingBasics, setIsEditingBasics] = React.useState(false);
  // In-app delete confirmation (replaces the native window.confirm).
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
  const [activityPendingDelete, setActivityPendingDelete] = React.useState<{ id: string; title: string } | null>(null);
  const [isDeletingActivity, setIsDeletingActivity] = React.useState(false);
  // Like/bookmark state for this trip (T2.14).
  const [reaction, setReaction] = React.useState<Reaction | undefined>(undefined);

  const tripUuid = React.useMemo(() => {
    if (!tripId || isDraft) return '';
    return tripId;
  }, [tripId, isDraft]);

  // Like/bookmark state (T2.14): trip reads arrive enriched with likes +
  // viewer state, so the reaction derives from the loaded trip — no extra
  // request (this also re-syncs on the post-confirm refetch).
  React.useEffect(() => {
    if (!tripUuid || !serverTrip) return;
    setReaction(tripReaction(serverTrip));
  }, [tripUuid, serverTrip]);

  const fetchConversation = React.useCallback(async (nextConversationId: string) => {
    const conversationsEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;

    if (!conversationsEndpoint) return;

    setIsLoadingConversation(true);
    try {
      const tokens = getTokensFromCache();

      if (!tokens?.idToken) {
        throw new Error('User not authenticated');
      }

      let endpoint = conversationsEndpoint;
      if (endpoint.endsWith('/conversations')) {
        endpoint = endpoint.replace(/\/conversations$/, '/conversation');
      } else {
        endpoint = endpoint.replace(/\/$/, '') + '/conversation';
      }

      const response = await fetch(`${endpoint}?conversation_id=${encodeURIComponent(nextConversationId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.statusText}`);
      }

      const data = await response.json();
      const formattedMessages: ChatMessage[] = (data.messages || []).map((message: any) => {
        const parsed = message.actor === 'assistant' ? parseAgentResponse(message.text || '') : { response: null, isError: false };

        return {
          id: message.message_id?.toString() || `history-${Date.now()}-${Math.random()}`,
          message: parsed.response?.message || message.text || '',
          isUser: message.actor === 'user',
          timestamp: new Date((message.created_at || Date.now() / 1000) * 1000),
          intent: parsed.response?.intent,
          trip_draft: parsed.response?.trip_draft,
          locations_recommendations: normalizeLocationRecommendations(parsed.response?.locations_recommendations),
          activities: parsed.response?.activities,
        };
      });

      setChatMessages(formattedMessages);
    } catch (err: any) {
      setChatMessages([{
        id: `conversation-error-${Date.now()}`,
        message: `Could not load conversation history: ${err.message}`,
        isUser: false,
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoadingConversation(false);
    }
  }, []);

  React.useEffect(() => {
    if (!tripUuid) return;

    let isMounted = true;

    const fetchTrip = async () => {
      try {
        setIsLoadingTrip(true);
        setTripError(null);
        const fetchedTrip = await getTripById(tripUuid);

        if (!isMounted) return;

        setServerTrip(fetchedTrip);
        setTripDescription(fetchedTrip.description || '');

        if (fetchedTrip.conversation_id) {
          setConversationId(fetchedTrip.conversation_id);
          fetchConversation(fetchedTrip.conversation_id);
        }
      } catch (err: any) {
        if (isMounted) {
          setTripError(err.message || 'Failed to load trip.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingTrip(false);
        }
      }
    };

    fetchTrip();

    return () => {
      isMounted = false;
    };
  }, [tripUuid, fetchConversation]);

  React.useEffect(() => {
    setTripBasics(buildTripBasicsForm(serverTrip));
  }, [serverTrip]);

  React.useEffect(() => {
    setSelectedActivityId(null);
    setSelectedRecommendation(null);
  }, [tripUuid]);

  React.useEffect(() => {
    if (!tripUuid) return;

    let isMounted = true;

    const fetchTripActivities = async () => {
      try {
        setIsLoadingActivities(true);
        setActivityError(null);
        const fetchedActivities = await getActivities(tripUuid);

        if (!isMounted) return;

        const fallbackDay = getDefaultTripDay(serverTrip);
        const mappedActivities = fetchedActivities.map((activity) => mapServerActivityToTripActivity(activity, fallbackDay));
        setActivities(mappedActivities);
      } catch (err: any) {
        if (isMounted) {
          setActivityError(err.message || 'Failed to load activities.');
          setActivities([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingActivities(false);
        }
      }
    };

    fetchTripActivities();

    return () => {
      isMounted = false;
    };
  }, [tripUuid, serverTrip]);

  React.useEffect(() => {
    if (!tripDayLabels.length) {
      setSelectedDay('');
      return;
    }

    if (!tripDayLabels.includes(selectedDay)) {
      setSelectedDay(tripDayLabels[0]);
    }
  }, [selectedDay, tripDayLabels]);

  React.useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isLoadingConversation]);

  const finishAssistantMessage = React.useCallback((
    message: string,
    isError = false,
    agentResponse?: AgentResponse
  ) => {
    setChatMessages((current) => {
      const updated = [...current];
      const lastMessageIndex = updated.length - 1;
      const messageData: Partial<ChatMessage> = {
        message,
        isLoading: false,
        isError,
        intent: agentResponse?.intent,
        trip_draft: agentResponse?.trip_draft,
        locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
        activities: agentResponse?.activities,
        action_request: agentResponse?.action_request,
      };

      if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
        updated[lastMessageIndex] = {
          ...updated[lastMessageIndex],
          ...messageData,
        };
        return updated;
      }

      return [
        ...updated,
        {
          id: `assistant-${Date.now()}`,
          message,
          isUser: false,
          timestamp: new Date(),
          isLoading: false,
          isError,
          intent: agentResponse?.intent,
          trip_draft: agentResponse?.trip_draft,
          locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
          activities: agentResponse?.activities,
          action_request: agentResponse?.action_request,
        },
      ];
    });

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    setIsWaitingForResponse(false);
  }, []);

  React.useEffect(() => {
    if (!canEditTrip) return undefined;

    return webSocket.subscribe((message: WebSocketMessage) => {
      const incomingConversationId = message.conversation_id || (message as any).conversationId;

      if (incomingConversationId) {
        setConversationId(incomingConversationId);
      }

      if (conversationId && incomingConversationId && incomingConversationId !== conversationId) {
        return;
      }

      const status = (message as any).status || (message as any).body?.status || (message as any).response?.status;
      const text =
        (message as any).text ||
        (message as any).body?.text ||
        (message as any).response?.text ||
        (message as any).message ||
        (message as any).response?.message;
      const error = (message as any).error || (message as any).body?.error || (message as any).response?.error;

      if (status === 'processing' || message.message === 'Connection accepted') {
        return;
      }

      if (error) {
        finishAssistantMessage(`Error: ${error}`, true);
        return;
      }

      if (status === 'complete' || text) {
        let responseText = text || '';
        let agentResponse: AgentResponse | null = null;

        if ((message as any).response && typeof (message as any).response === 'object' && (message as any).response.intent) {
          agentResponse = (message as any).response as AgentResponse;
          responseText = agentResponse.message;
        } else {
          const parsed = parseAgentResponse(responseText);
          agentResponse = parsed.response;
          responseText = agentResponse?.message || responseText;
        }

        finishAssistantMessage(responseText, false, agentResponse || undefined);
      }
    });
  }, [canEditTrip, conversationId, finishAssistantMessage, webSocket]);

  React.useEffect(() => () => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
  }, []);

  const handleActivityDraftChange = (
    field: keyof TripActivityDraft,
    value: string
  ) => {
    setActivityDraft((current) => ({ ...current, [field]: value }));
  };

  const handleTripBasicsChange = (field: keyof TripBasicsForm, value: string | string[]) => {
    setTripBasics((current) => ({ ...current, [field]: value }));
    setTripBasicsError(null);
  };

  const handleDestinationChange = (index: number, value: string) => {
    const destinations = [...tripBasics.destinations];
    destinations[index] = value;
    handleTripBasicsChange('destinations', destinations);
  };

  const handleAddDestination = () => {
    handleTripBasicsChange('destinations', [...tripBasics.destinations, '']);
  };

  const handleRemoveDestination = (index: number) => {
    const nextDestinations = tripBasics.destinations.filter((_, currentIndex) => currentIndex !== index);
    handleTripBasicsChange('destinations', nextDestinations.length ? nextDestinations : ['']);
  };

  const validateTripBasics = () => {
    const destinations = tripBasics.destinations.filter((destination) => destination.trim());

    if (!tripBasics.title.trim()) return 'Trip title is required.';
    if (!destinations.length) return 'At least one destination is required.';
    if (!tripBasics.startDate || !tripBasics.endDate) return 'Trip dates are required.';
    if (parseLocalDate(tripBasics.startDate) > parseLocalDate(tripBasics.endDate)) return 'Start date must be before end date.';
    if (tripBasics.budget && Number(tripBasics.budget) < 0) return 'Budget must be greater than or equal to 0.';
    if (!Number.isInteger(Number(tripBasics.adults)) || Number(tripBasics.adults) < 1) return 'Adults must be a whole number of at least 1.';
    if (!Number.isInteger(Number(tripBasics.children)) || Number(tripBasics.children) < 0) return 'Children must be a whole number of 0 or more.';

    return null;
  };

  const handleSaveTripBasics = async () => {
    if (!canEditTrip) return;

    const validationError = validateTripBasics();
    if (validationError) {
      setTripBasicsError(validationError);
      return;
    }

    const budgetValue = Number(tripBasics.budget);
    const tripPayload: CreateTripRequest = {
      title: tripBasics.title.trim(),
      destinations: tripBasics.destinations.filter((destination) => destination.trim()),
      dates: buildTripDates(tripBasics.startDate, tripBasics.endDate),
      status: serverTrip?.status === 'completed' ? 'completed' : 'planning',
      budget: Number.isFinite(budgetValue) && budgetValue > 0
        ? { amount: budgetValue, currency: tripBasics.budgetCurrency }
        : undefined,
      travelers: { adults: Number(tripBasics.adults), children: Number(tripBasics.children) },
      description: tripDescription || undefined,
    };

    try {
      setIsSavingTripBasics(true);
      setTripBasicsError(null);

      if (isDraft) {
        const createdTrip = await createTrip(tripPayload);
        setServerTrip(createdTrip);
        navigate(`/trips/${String(createdTrip.id).replace('TRIP#', '')}`, {
          replace: true,
          state: { backTo: '/trips', owned: true, trip: createdTrip },
        });
      } else if (tripUuid) {
        const updatedTrip = await updateTrip({
          id: tripUuid,
          ...tripPayload,
        });
        setServerTrip(updatedTrip);
        setIsEditingBasics(false);
      }
    } catch (err: any) {
      setTripBasicsError(err.message || 'Failed to save trip details.');
    } finally {
      setIsSavingTripBasics(false);
    }
  };

  const handleDeleteTrip = () => {
    if (!canEditTrip || !tripUuid || isDraft) return;
    setIsConfirmingDelete(true);
  };

  const confirmDeleteTrip = async () => {
    if (!canEditTrip || !tripUuid || isDraft) return;

    try {
      setIsSavingTripBasics(true);
      await deleteTrip(tripUuid);
      navigate('/trips', { replace: true });
    } catch (err: any) {
      setTripBasicsError(err.message || 'Failed to delete trip.');
      setIsConfirmingDelete(false);
    } finally {
      setIsSavingTripBasics(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!canEditTrip || !tripUuid) return;

    if (!activityDraft.description.trim() || !activityDraft.startTime || !activityDraft.endTime) {
      return;
    }

    const activityData: CreateActivityRequest = {
      title: activityDraft.description,
      location: activityDraft.place || activityDraft.description,
      dateTime: buildActivityDateTime(selectedDay, activityDraft.startTime, serverTrip),
      endDateTime: buildActivityDateTime(selectedDay, activityDraft.endTime, serverTrip),
      budget: parseActivityBudget(activityDraft.budget),
    };

    try {
      if (editingActivityId) {
        const activityUuid = extractActivityUuid(editingActivityId);
        const updatedActivity = await updateActivity(tripUuid, activityUuid, activityData);
        const mappedActivity = mapServerActivityToTripActivity(updatedActivity, selectedDay);
        setActivities((current) =>
          current.map((activity) => (activity.id === editingActivityId ? mappedActivity : activity))
        );
        setEditingActivityId(null);
      } else {
        const createdActivity = await createActivity(tripUuid, activityData);
        const mappedActivity = mapServerActivityToTripActivity(createdActivity, selectedDay);
        setActivities((current) => [...current, mappedActivity]);
        setSelectedActivityId(mappedActivity.id);
      }

      setActivityDraft(createEmptyActivityDraft());
      setIsActivityModalOpen(false);
      // Server re-timed the day (new/changed legs) — pull the recomputed schedule.
      void reloadActivities();
    } catch (err: any) {
      setActivityError(err.message || 'Failed to save activity.');
    }
  };

  const handleAddActivity = () => {
    if (!canEditTrip) return;

    setEditingActivityId(null);
    setActivityDraft(createEmptyActivityDraft());
    setIsActivityModalOpen(true);
  };

  const handleEditActivity = (activity: TripActivity) => {
    if (!canEditTrip) return;

    setEditingActivityId(activity.id);
    setActivityDraft({
      description: activity.description,
      startTime: activity.startTime,
      endTime: activity.endTime,
      place: activity.place || '',
      budget: activity.budget || '',
      category: activity.category,
    });
    setIsActivityModalOpen(true);
  };

  const handleRemoveActivity = (activityId: string) => {
    if (!canEditTrip) return;

    const activity = activities.find((item) => item.id === activityId);
    setActivityPendingDelete({
      id: activityId,
      title: activity?.description || 'this activity',
    });
  };

  const confirmDeleteActivity = async () => {
    if (!canEditTrip || !tripUuid || !activityPendingDelete) return;

    const { id: activityId } = activityPendingDelete;

    try {
      setIsDeletingActivity(true);
      await deleteActivity(tripUuid, extractActivityUuid(activityId));
      setActivities((current) => current.filter((activity) => activity.id !== activityId));
      // Removing a stop re-stitches the day server-side — pull the new times.
      void reloadActivities();
      if (selectedActivityId === activityId) {
        setSelectedActivityId(null);
      }
      if (editingActivityId === activityId) {
        setEditingActivityId(null);
        setActivityDraft(createEmptyActivityDraft());
        setIsActivityModalOpen(false);
      }
      setActivityPendingDelete(null);
    } catch (err: any) {
      setActivityError(err.message || 'Failed to delete activity.');
    } finally {
      setIsDeletingActivity(false);
    }
  };

  // Reload the day's activities from the server. A structural edit (add/remove)
  // or a leg-mode change triggers a server-side reschedule that shifts multiple
  // downstream stops' times, so a local patch isn't enough — refetch the truth.
  const reloadActivities = React.useCallback(async () => {
    if (!tripUuid) return;
    try {
      const fetched = await getActivities(tripUuid);
      const fallbackDay = getDefaultTripDay(serverTrip);
      setActivities(fetched.map((activity) => mapServerActivityToTripActivity(activity, fallbackDay)));
    } catch (err: any) {
      setActivityError(err.message || 'Failed to reload activities.');
    }
  }, [tripUuid, serverTrip]);

  // Change the transport mode for a stop's inbound leg (T1.16). The server
  // recomputes that leg from Google in the new mode and re-times the day.
  const handleChangeTravelMode = React.useCallback(async (activityId: string, mode: TravelMode) => {
    if (!canEditTrip || !tripUuid) return;
    await setActivityTravelMode(tripUuid, extractActivityUuid(activityId), mode);
    await reloadActivities();
  }, [canEditTrip, tripUuid, reloadActivities]);

  const handleCancelEdit = () => {
    setEditingActivityId(null);
    setActivityDraft(createEmptyActivityDraft());
    setIsActivityModalOpen(false);
  };

  const handleClearMapPlace = () => {
    setSelectedActivityId(null);
    setSelectedRecommendation(null);
  };

  const handleSelectActivity = (activityId: string) => {
    setSelectedRecommendation(null);
    setSelectedActivityId(activityId);
  };

  const handleSelectDay = (day: string) => {
    setSelectedDay(day);
    setSelectedRecommendation(null);
    setSelectedActivityId(null);
  };

  const handleDescriptionSave = async (nextDescription: string) => {
    setTripDescription(nextDescription);

    if (!canEditTrip || !tripUuid) return;

    try {
      const updatedTrip = await updateTrip({
        id: tripUuid,
        description: nextDescription,
      });
      setServerTrip(updatedTrip);
    } catch (err) {
      setTripDescription(displayedTrip.description);
    }
  };

  // T1.3: freeze the proposal card after the user acts; on confirm, refetch
  // the trip so activities/fields reflect the applied change immediately.
  const resolveActionRequest = React.useCallback((
    messageId: string,
    resolution: ActionResolution,
    note: string,
    createdTripId?: string,
  ) => {
    setChatMessages((current) => {
      const updated = current.map((message) => (
        message.id === messageId
          ? {
              ...message,
              actionResolution: resolution,
              createdTripId,
              actionResolutionNote: note,
            }
          : message
      ));

      if (resolution !== 'dismissed' && !createdTripId) {
        updated.push({
          id: `action-${resolution}-${Date.now()}`,
          message: resolution === 'confirmed' ? `✓ ${note}` : `⚠ ${note}`,
          isUser: false,
          timestamp: new Date(),
          isError: resolution === 'failed',
        });
      }

      return updated;
    });
    if (resolution === 'confirmed' && tripUuid) {
      getTripById(tripUuid)
        .then((freshTrip) => setServerTrip(freshTrip))
        .catch(() => undefined);
    }
  }, [tripUuid]);

  const handleSendChatMessage = async (message = currentInput) => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || !canEditTrip || isWaitingForResponse) return;

    setCurrentInput('');
    setChatMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        message: trimmedMessage,
        isUser: true,
        timestamp: new Date(),
      },
      {
        id: `loading-${Date.now()}`,
        message: '',
        isUser: false,
        timestamp: new Date(),
        isLoading: true,
      },
    ]);
    setIsWaitingForResponse(true);

    try {
      await webSocket.sendMessage(trimmedMessage, conversationId || serverTrip?.conversation_id, {
        user_time: new Date().toISOString(),
        trip_draft: serverTrip
          ? {
              // id lets the agent populate action_request.trip_id (T1.3)
              id: (serverTrip as any).id,
              title: serverTrip.title,
              destinations: serverTrip.destinations,
              dates: serverTrip.dates,
              budget: serverTrip.budget,
              travelers: serverTrip.travelers,
              description: tripDescription,
            }
          : undefined,
      });

      responseTimeoutRef.current = setTimeout(() => {
        finishAssistantMessage('The planner is taking longer than expected. Please try again.', true);
      }, 60000);
    } catch (err: any) {
      finishAssistantMessage(err.message || 'Could not send message to the planner.', true);
    }
  };

  return (
    <DashboardLayout activeSection="trips" wide>
      <div className={`v2-trip-detail-page ${canEditTrip && !isDraft && !isLoadingTrip && !tripError ? 'v2-trip-detail-page--planner' : ''}`}>
        {isLoadingTrip && (
          <div className="v2-trip-detail-page__state">
            <span className="v2-trip-detail-page__spinner" />
            <p>Loading trip details...</p>
          </div>
        )}
        {!isLoadingTrip && tripError && (
          <div className="v2-trip-detail-page__state v2-trip-detail-page__state--error">
            <p>{tripError}</p>
            <button type="button" onClick={() => navigate(backTo || '/trips')}>Back to trips</button>
          </div>
        )}
        {!isLoadingTrip && !tripError && (
          <>
        <div className={`v2-trip-detail-page__workspace ${canEditTrip ? 'v2-trip-detail-page__workspace--owned' : 'v2-trip-detail-page__workspace--public'} ${isDraft ? 'v2-trip-detail-page__workspace--draft' : ''}`}>
          {canEditTrip ? (
            <>
              {isDraft ? (
                <>
                  <TripDetailHeader
                    displayedTrip={displayedTrip}
                    isDraft={isDraft}
                    canEditTrip={canEditTrip}
                    tripBasics={tripBasics}
                    tripUuid={tripUuid}
                    reaction={reaction}
                    budgetBreakdown={undefined}
                    onEditBasics={() => {
                      setTripBasics(buildTripBasicsForm(serverTrip));
                      setTripBasicsError(null);
                      setIsEditingBasics(true);
                    }}
                    onPrimaryAction={handleSaveTripBasics}
                    primaryActionLabel="Create this trip"
                  />
                  <div className="v2-trip-detail-page__setup">
                    <TripBasicsEditor
                      value={tripBasics}
                      error={tripBasicsError}
                      isSaving={isSavingTripBasics}
                      isDraft={isDraft}
                      onChange={handleTripBasicsChange}
                      onDestinationChange={handleDestinationChange}
                      onAddDestination={handleAddDestination}
                      onRemoveDestination={handleRemoveDestination}
                      onSave={handleSaveTripBasics}
                      onDelete={handleDeleteTrip}
                    />
                    <TripDescriptionEditor
                      value={tripDescription}
                      onChange={handleDescriptionSave}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="v2-trip-detail-page__main-column">
                    <TripDetailHeader
                      displayedTrip={displayedTrip}
                      isDraft={isDraft}
                      canEditTrip={canEditTrip}
                      tripBasics={tripBasics}
                      tripUuid={tripUuid}
                      reaction={reaction}
                      budgetBreakdown={serverTrip ? undefined : budgetBreakdown}
                      onEditBasics={() => {
                        setTripBasics(buildTripBasicsForm(serverTrip));
                        setTripBasicsError(null);
                        setIsEditingBasics(true);
                      }}
                      onPrimaryAction={() => navigate('/calendar')}
                      primaryActionLabel="Full calendar"
                    />

                    <main className="v2-trip-detail-page__itinerary" aria-label="Trip itinerary">
                      <DayPlan
                        activities={activities}
                        days={tripDayLabels}
                        selectedDay={selectedDay}
                        tripTitle={displayedTrip.title}
                        tripStartDate={serverTrip?.dates?.startDate}
                        selectedActivityId={selectedActivityId}
                        onAdd={handleAddActivity}
                        onSelectDay={handleSelectDay}
                        onSelectActivity={handleSelectActivity}
                        onEdit={handleEditActivity}
                        onRemove={handleRemoveActivity}
                        onChangeTravelMode={handleChangeTravelMode}
                        readOnly={false}
                        isLoading={isLoadingActivities}
                        error={activityError}
                      />
                    </main>
                  </div>

                  <aside className="v2-trip-detail-page__assistant" aria-label="Map and planning chat">
                    <TripMapSidebar
                      mapOnly
                      destinations={displayedTrip.stops}
                      activities={activities}
                      selectedDay={selectedDay}
                      selectedActivityId={selectedActivityId}
                      onSelectActivity={handleSelectActivity}
                      onClearPlace={handleClearMapPlace}
                      selectedExternalPlaceId={selectedRecommendation?.place_id || selectedRecommendation?.name || null}
                      selectedExternalPlaceQuery={selectedRecommendation?.name}
                    />

                    <section className="v2-trip-chat v2-trip-chat--compact" aria-label="Planning chat">
                      <div className="v2-trip-chat__status">
                        <span aria-hidden>✣</span>
                        <h2>Planning with AI</h2>
                        <p>{webSocket.isConnected ? 'Connected' : webSocket.isConnecting ? 'Connecting' : 'Offline'}</p>
                      </div>

                      <div className="v2-trip-chat__messages">
                        {isLoadingConversation && <ChatStateMessage message="Loading conversation history..." />}
                        {!isLoadingConversation && chatMessages.length === 0 && (
                          <AssistantBubble>
                            Ask me to adjust this trip, add activities, refine the route, or update the budget.
                          </AssistantBubble>
                        )}
                        {chatMessages.map((message) => (
                          message.isUser ? (
                            <UserBubble key={message.id}>{message.message}</UserBubble>
                          ) : (
                            <React.Fragment key={message.id}>
                              <AssistantBubble isLoading={message.isLoading} isError={message.isError}>
                                {message.isLoading ? 'Planning...' : message.message}
                              </AssistantBubble>
                              {!message.isLoading && message.action_request && (
                                <ChatAttachment>
                                  <ActionRequestCard
                                    request={message.action_request}
                                    resolution={message.actionResolution}
                                    createdTripId={message.createdTripId}
                                    resolutionNote={message.actionResolutionNote}
                                    backTo={backTo || `/trips/${tripUuid}`}
                                    conversationId={conversationId || serverTrip?.conversation_id || undefined}
                                    existingTripId={tripUuid || undefined}
                                    fallbackActivities={collectFallbackActivities(message)}
                                    onResolve={(resolution, note, tripId) => resolveActionRequest(message.id, resolution, note, tripId)}
                                  />
                                </ChatAttachment>
                              )}
                              {!message.isLoading && message.locations_recommendations && message.locations_recommendations.length > 0 && (
                                <div className="v2-trip-chat__recommendations">
                                  {message.locations_recommendations.map((recommendation) => (
                                    <button
                                      type="button"
                                      key={`${message.id}-${recommendation.place_id || recommendation.name}`}
                                      className={`v2-trip-chat__recommendation ${
                                        selectedRecommendation?.name === recommendation.name
                                        && selectedRecommendation?.place_id === recommendation.place_id
                                          ? 'v2-trip-chat__recommendation--active'
                                          : ''
                                      }`}
                                      onClick={() => {
                                        setSelectedActivityId(null);
                                        setSelectedRecommendation(recommendation);
                                      }}
                                    >
                                      {recommendation.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          )
                        ))}
                        <div ref={chatMessagesEndRef} />
                      </div>

                      <div className="v2-trip-chat__input">
                        <input
                          type="text"
                          value={currentInput}
                          onChange={(event) => setCurrentInput(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleSendChatMessage();
                            }
                          }}
                          placeholder="Message the planner..."
                          aria-label="Message the planner"
                          disabled={!webSocket.isConnected || isWaitingForResponse}
                        />
                        <button
                          type="button"
                          aria-label="Send message"
                          onClick={() => handleSendChatMessage()}
                          disabled={!webSocket.isConnected || isWaitingForResponse || !currentInput.trim()}
                        >
                          ↗
                        </button>
                      </div>
                    </section>
                  </aside>
                </>
              )}
            </>
          ) : (
            <>
              <TripDetailHeader
                displayedTrip={displayedTrip}
                isDraft={false}
                canEditTrip={false}
                tripBasics={tripBasics}
                tripUuid={tripUuid}
                reaction={reaction}
                budgetBreakdown={serverTrip ? undefined : budgetBreakdown}
                onEditBasics={() => undefined}
                onPrimaryAction={() => navigate('/calendar')}
                primaryActionLabel="Full calendar"
              />
              <PublicTripDetails
                description={displayedTrip.description}
                photos={[displayedTrip.imageUrl, ...publicTripPhotos.filter((photo) => photo !== displayedTrip.imageUrl)]}
              />

              <aside className="v2-trip-detail-sidebar" aria-label="Trip details">
                <TripMapSidebar
                  destinations={displayedTrip.stops}
                  activities={activities}
                  selectedDay={selectedDay}
                  selectedActivityId={selectedActivityId}
                  onSelectActivity={setSelectedActivityId}
                />
                <DayPlan
                  activities={activities}
                  days={tripDayLabels}
                  selectedDay={selectedDay}
                  tripTitle={displayedTrip.title}
                  tripStartDate={serverTrip?.dates?.startDate}
                  selectedActivityId={selectedActivityId}
                  onAdd={handleAddActivity}
                  onSelectDay={handleSelectDay}
                  onSelectActivity={setSelectedActivityId}
                  onEdit={handleEditActivity}
                  onRemove={handleRemoveActivity}
                  readOnly
                  isLoading={isLoadingActivities}
                  error={activityError}
                />
              </aside>
            </>
          )}
        </div>

        {isActivityModalOpen && canEditTrip && (
          <ActivityEditorModal
            draft={activityDraft}
            editingActivityId={editingActivityId}
            onDraftChange={handleActivityDraftChange}
            onSave={handleSaveActivity}
            onCancel={handleCancelEdit}
          />
        )}

        {isEditingBasics && canEditTrip && !isDraft && (
          <div className="v2-trip-activity-modal" role="dialog" aria-modal="true" aria-labelledby="trip-basics-modal-title">
            <div className="v2-trip-activity-modal__backdrop" onClick={() => setIsEditingBasics(false)} />
            <section className="v2-trip-activity-modal__panel">
              <header>
                <div>
                  <p>Trip details</p>
                  <h2 id="trip-basics-modal-title">Edit trip</h2>
                </div>
                <button type="button" onClick={() => setIsEditingBasics(false)} aria-label="Close trip editor">×</button>
              </header>
              <TripBasicsEditor
                value={tripBasics}
                error={tripBasicsError}
                isSaving={isSavingTripBasics}
                isDraft={isDraft}
                onChange={handleTripBasicsChange}
                onDestinationChange={handleDestinationChange}
                onAddDestination={handleAddDestination}
                onRemoveDestination={handleRemoveDestination}
                onSave={handleSaveTripBasics}
                onDelete={handleDeleteTrip}
              />
            </section>
          </div>
        )}

        {isConfirmingDelete && canEditTrip && !isDraft && (
          <div
            className="v2-trip-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-trip-title"
            aria-describedby="delete-trip-desc"
          >
            <div
              className="v2-trip-confirm-modal__backdrop"
              onClick={() => { if (!isSavingTripBasics) setIsConfirmingDelete(false); }}
            />
            <section className="v2-trip-confirm-modal__panel">
              <h2 id="delete-trip-title" className="v2-trip-confirm-modal__title">Delete this trip?</h2>
              <p id="delete-trip-desc" className="v2-trip-confirm-modal__desc">
                This cannot be undone — the trip and all of its activities will be permanently
                deleted. It cannot be recreated.
              </p>
              <div className="v2-trip-confirm-modal__actions">
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => setIsConfirmingDelete(false)}
                  disabled={isSavingTripBasics}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__danger"
                  onClick={confirmDeleteTrip}
                  disabled={isSavingTripBasics}
                >
                  {isSavingTripBasics ? 'Deleting…' : 'Delete trip'}
                </button>
              </div>
            </section>
          </div>
        )}

        {activityPendingDelete && canEditTrip && !isDraft && (
          <div
            className="v2-trip-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-activity-title"
            aria-describedby="delete-activity-desc"
          >
            <div
              className="v2-trip-confirm-modal__backdrop"
              onClick={() => { if (!isDeletingActivity) setActivityPendingDelete(null); }}
            />
            <section className="v2-trip-confirm-modal__panel">
              <h2 id="delete-activity-title" className="v2-trip-confirm-modal__title">Remove this activity?</h2>
              <p id="delete-activity-desc" className="v2-trip-confirm-modal__desc">
                <strong>{activityPendingDelete.title}</strong> will be permanently removed from your
                itinerary. This cannot be undone.
              </p>
              <div className="v2-trip-confirm-modal__actions">
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => setActivityPendingDelete(null)}
                  disabled={isDeletingActivity}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__danger"
                  onClick={confirmDeleteActivity}
                  disabled={isDeletingActivity}
                >
                  {isDeletingActivity ? 'Removing…' : 'Remove activity'}
                </button>
              </div>
            </section>
          </div>
        )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

interface TripDescriptionEditorProps {
  value: string;
  onChange: (value: string) => void;
}

interface TripBasicsEditorProps {
  value: TripBasicsForm;
  error: string | null;
  isSaving: boolean;
  isDraft: boolean;
  onChange: (field: keyof TripBasicsForm, value: string | string[]) => void;
  onDestinationChange: (index: number, value: string) => void;
  onAddDestination: () => void;
  onRemoveDestination: (index: number) => void;
  onSave: () => void;
  onDelete: () => void;
}

const TripBasicsEditor: React.FC<TripBasicsEditorProps> = ({
  value,
  error,
  isSaving,
  isDraft,
  onChange,
  onDestinationChange,
  onAddDestination,
  onRemoveDestination,
  onSave,
  onDelete,
}) => (
  <section className="v2-trip-basics-card" aria-labelledby="trip-basics-heading">
    <div className="v2-trip-basics-card__header">
      <div>
        <p>Trip setup</p>
        <h2 id="trip-basics-heading">Details</h2>
      </div>
      <div className="v2-trip-basics-card__actions">
        <button type="button" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : isDraft ? 'Create trip' : 'Save details'}
        </button>
        {!isDraft && (
          <button type="button" className="v2-trip-basics-card__danger" onClick={onDelete} disabled={isSaving}>
            Delete trip
          </button>
        )}
      </div>
    </div>

    <div className="v2-trip-basics-card__grid">
      <label className="v2-trip-basics-card__full">
        Trip title
        <input
          type="text"
          value={value.title}
          placeholder="Trip title"
          onChange={(event) => onChange('title', event.target.value)}
        />
      </label>
      <label>
        Start date
        <input
          type="date"
          value={value.startDate}
          onChange={(event) => onChange('startDate', event.target.value)}
        />
      </label>
      <label>
        End date
        <input
          type="date"
          value={value.endDate}
          onChange={(event) => onChange('endDate', event.target.value)}
        />
      </label>
      <label>
        Budget
        <input
          type="number"
          min="0"
          value={value.budget}
          placeholder="1500"
          onChange={(event) => onChange('budget', event.target.value)}
        />
      </label>
      <label>
        Currency
        <select value={value.budgetCurrency} onChange={(event) => onChange('budgetCurrency', event.target.value)}>
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
          <option value="JPY">JPY (¥)</option>
          <option value="CAD">CAD ($)</option>
          <option value="AUD">AUD ($)</option>
        </select>
      </label>
      <label>
        Adults
        <input
          type="number"
          min="1"
          step="1"
          value={value.adults}
          onChange={(event) => onChange('adults', event.target.value)}
        />
      </label>
      <label>
        Children
        <input
          type="number"
          min="0"
          step="1"
          value={value.children}
          onChange={(event) => onChange('children', event.target.value)}
        />
      </label>
      <div className="v2-trip-basics-card__destinations">
        <span>Destinations</span>
        {value.destinations.map((destination, index) => (
          <div key={`${index}-${destination}`}>
            <input
              type="text"
              value={destination}
              placeholder="Destination"
              onChange={(event) => onDestinationChange(index, event.target.value)}
            />
            <button type="button" onClick={() => onRemoveDestination(index)} aria-label="Remove destination">
              ×
            </button>
          </div>
        ))}
        <button type="button" onClick={onAddDestination}>+ Add destination</button>
      </div>
    </div>
    {error && <p className="v2-trip-basics-card__error">{error}</p>}
  </section>
);

const TripDescriptionEditor: React.FC<TripDescriptionEditorProps> = ({ value, onChange }) => {
  const [draft, setDraft] = React.useState(value);

  const handleSave = () => {
    onChange(draft.trim());
  };

  const handleCancel = () => {
    setDraft(value);
  };

  return (
    <section className="v2-trip-description-card" aria-labelledby="trip-description-heading">
      <div className="v2-trip-description-card__header">
        <div>
          <p>Trip story</p>
          <h2 id="trip-description-heading">Description</h2>
        </div>
        <div>
          <button type="button" onClick={handleCancel}>Cancel</button>
          <button type="button" onClick={handleSave}>Save</button>
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Tell people what makes this trip special..."
        rows={4}
      />
    </section>
  );
};

interface PublicTripDetailsProps {
  description: string;
  photos: string[];
}

const PublicTripDetails: React.FC<PublicTripDetailsProps> = ({
  description,
  photos,
}) => (
  <main className="v2-trip-public-details" aria-label="Trip details and photos">
    <section>
      <p>Trip story</p>
      <p className="v2-trip-public-details__description">{description}</p>
    </section>

    <section>
      <p>Photos</p>
      <div className="v2-trip-public-details__photos">
        {photos.slice(0, 4).map((photo) => (
          <img key={photo} src={photo} alt="" />
        ))}
      </div>
    </section>
  </main>
);

const TRIP_TITLE_MAX_LENGTH = 50;

const truncateTripTitle = (title: string): string => {
  if (title.length <= TRIP_TITLE_MAX_LENGTH) return title;
  return `${title.slice(0, TRIP_TITLE_MAX_LENGTH)}...`;
};

interface TripDetailHeaderProps {
  displayedTrip: {
    title: string;
    status: string;
    dateRange: string;
    duration: string;
    stops: string[];
    budget: string;
    travelers: string | number;
    imageUrl: string;
  };
  isDraft: boolean;
  canEditTrip: boolean;
  tripBasics: TripBasicsForm;
  tripUuid: string;
  reaction?: Reaction;
  budgetBreakdown?: Array<{ label: string; value: string }>;
  onEditBasics: () => void;
  onPrimaryAction: () => void;
  primaryActionLabel: string;
}

const TripDetailHeader: React.FC<TripDetailHeaderProps> = ({
  displayedTrip,
  isDraft,
  canEditTrip,
  tripBasics,
  tripUuid,
  reaction,
  budgetBreakdown,
  onEditBasics,
  onPrimaryAction,
  primaryActionLabel,
}) => (
  <header className="v2-trip-detail-page__header">
    <div className="v2-trip-detail-page__header-main">
      {!isDraft && (
        <img src={displayedTrip.imageUrl} alt="" className="v2-trip-detail-page__thumb" />
      )}
      {isDraft && <div className="v2-trip-detail-page__thumb v2-trip-detail-page__thumb--empty" aria-hidden />}
      <div className="v2-trip-detail-page__header-body">
        <div className="v2-trip-detail-page__header-row v2-trip-detail-page__header-row--top">
          <div className="v2-trip-detail-page__header-identity">
            <h1>
              <span
                className={`v2-trip-detail-page__title-text ${isDraft && !tripBasics.title.trim() ? 'v2-trip-detail-page__title--placeholder' : ''}`}
                title={displayedTrip.title}
              >
                {truncateTripTitle(displayedTrip.title)}
              </span>
              <span className={`v2-trip-detail-page__status ${isDraft ? 'v2-trip-detail-page__status--draft' : ''}`}>
                {isDraft ? 'DRAFT · NOT CREATED' : displayedTrip.status}
              </span>
              {canEditTrip && !isDraft && (
                <button
                  type="button"
                  className="v2-trip-detail-page__edit-basics"
                  onClick={onEditBasics}
                  aria-label="Edit trip details"
                  title="Edit trip details"
                >
                  ✎
                </button>
              )}
            </h1>
          </div>
        </div>

        <div className="v2-trip-detail-page__meta">
          <span className="v2-trip-detail-page__meta-dates">
            {isDraft ? displayedTrip.dateRange : `${displayedTrip.dateRange} · ${displayedTrip.duration}`}
          </span>
          <div className="v2-trip-detail-page__meta-stats">
            <MetricInline label="Budget" value={displayedTrip.budget} breakdown={isDraft ? undefined : budgetBreakdown} />
            <MetricInline
              label="Travelers"
              value={isDraft ? formatDraftTravelers(tripBasics) : String(displayedTrip.travelers)}
            />
            <MetricInline
              label="Stops"
              value={isDraft && displayedTrip.stops.length === 0 ? '—' : String(displayedTrip.stops.length)}
            />
          </div>
        </div>

        <div className="v2-trip-detail-page__header-row v2-trip-detail-page__header-row--bottom">
          {displayedTrip.stops.length > 0 && (
            <div className="v2-trip-detail-page__chips">
              {displayedTrip.stops.map((stop) => (
                <span key={stop}>{stop}</span>
              ))}
            </div>
          )}

          <div className="v2-trip-detail-page__toolbar">
            {!isDraft && tripUuid && (
              <ReactionBar tripId={tripUuid} reaction={reaction} />
            )}
            {canEditTrip && (
              <>
                <button type="button" className="v2-trip-detail-page__icon-btn" aria-label="Share trip">
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                    <path
                      d="M12 3v12M8 7l4-4 4 4M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="v2-trip-detail-page__icon-btn v2-trip-detail-page__icon-btn--primary"
                  onClick={onPrimaryAction}
                  aria-label={primaryActionLabel}
                  title={primaryActionLabel}
                >
                  {isDraft ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                      <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </header>
);

interface MetricInlineProps {
  label: string;
  value: string;
  breakdown?: Array<{ label: string; value: string }>;
}

const MetricInline: React.FC<MetricInlineProps> = ({ label, value, breakdown }) => (
  <span className="v2-trip-detail-page__meta-item" tabIndex={breakdown ? 0 : undefined}>
    <span className="v2-trip-detail-page__meta-label">{label}</span>
    <strong>{value}</strong>
    {breakdown && (
      <span className="v2-trip-detail-page__budget-popover" role="tooltip">
        <span className="v2-trip-detail-page__budget-popover-title">Budget breakdown</span>
        {breakdown.map((item) => (
          <span key={item.label} className="v2-trip-detail-page__budget-popover-row">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </span>
        ))}
      </span>
    )}
  </span>
);

interface BubbleProps {
  children: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}

const AssistantBubble: React.FC<BubbleProps> = ({ children, isLoading = false, isError = false }) => {
  const messageText = typeof children === 'string' ? children : null;

  return (
    <div className={`v2-trip-chat__row v2-trip-chat__row--assistant ${isError ? 'v2-trip-chat__row--error' : ''} ${isLoading ? 'v2-trip-chat__row--loading' : ''}`}>
      <span className="v2-trip-chat__spark">✦</span>
      {messageText && !isLoading ? (
        <FormattedChatMessage text={messageText} className="v2-trip-chat__bubble" />
      ) : (
        <p>{children}</p>
      )}
    </div>
  );
};

const UserBubble: React.FC<BubbleProps> = ({ children }) => (
  <div className="v2-trip-chat__row v2-trip-chat__row--user">
    <p>{children}</p>
  </div>
);

const ChatStateMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="v2-trip-chat__state">
    {message}
  </div>
);

// The travel connector between two consecutive stops (T1.16): vertical dotted
// rail on the left, then the leg's time + mode and (when editable) a mode picker.
const TRAVEL_MODE_LABEL: Record<string, string> = { drive: 'Drive', walk: 'Walk', transit: 'Transit' };
const TRAVEL_MODES: TravelMode[] = ['drive', 'walk', 'transit'];

const TravelLeg: React.FC<{
  leg: ActivityTravelLeg;
  readOnly?: boolean;
  onChangeMode?: (mode: TravelMode) => Promise<void>;
}> = ({ leg, readOnly, onChangeMode }) => {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const label = TRAVEL_MODE_LABEL[leg.mode] || leg.mode;
  const fmtMin = (min: number) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min`.replace(' 0 min', '') : `${min} min`);
  const summary = leg.minutes != null ? `≈ ${fmtMin(leg.minutes)} · ${label}` : `No ${label.toLowerCase()} route`;
  const distance = leg.km != null ? ` · ${leg.km} km` : '';
  // A cross-day leg is advisory: the drive across a day boundary. Inbound legs
  // (top of a day) note where they came "from"; the end-of-day outbound leg
  // notes where it goes "to" tomorrow's first stop.
  const fromNote = leg.crossDay && leg.fromLabel ? ` · from ${leg.fromLabel}` : '';
  const toNote = leg.crossDay && leg.toLabel ? ` · to ${leg.toLabel}` : '';

  const pick = async (mode: TravelMode) => {
    if (!onChangeMode || busy) return;
    setBusy(true);
    try {
      await onChangeMode(mode);
      setEditing(false);
    } catch {
      /* surfaced by the page-level error state */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`v2-trip-leg ${leg.crossDay ? 'v2-trip-leg--cross' : ''}`}>
      <span className="v2-trip-leg__rail" aria-hidden="true" />
      <div className="v2-trip-leg__body">
        {!editing ? (
          <>
            <span className="v2-trip-leg__summary">{summary}{distance}{fromNote}{toNote}</span>
            {!readOnly && onChangeMode && (
              <button type="button" className="v2-trip-leg__edit" onClick={() => setEditing(true)}>Edit</button>
            )}
          </>
        ) : (
          <div className="v2-trip-leg__modes" role="group" aria-label="Travel mode">
            {TRAVEL_MODES.map((m) => (
              <button
                key={m}
                type="button"
                disabled={busy}
                className={`v2-trip-leg__mode ${leg.chosenMode === m || (!leg.chosenMode && leg.mode === m) ? 'v2-trip-leg__mode--active' : ''}`}
                onClick={() => pick(m)}
              >
                {TRAVEL_MODE_LABEL[m]}
              </button>
            ))}
            <button type="button" className="v2-trip-leg__cancel" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

interface DayPlanProps {
  activities: TripActivity[];
  days: string[];
  selectedDay: string;
  tripTitle: string;
  tripStartDate?: string;
  selectedActivityId: string | null;
  onAdd: () => void;
  onSelectDay: (day: string) => void;
  onSelectActivity: (activityId: string) => void;
  onEdit: (activity: TripActivity) => void;
  onRemove: (activityId: string) => void;
  onChangeTravelMode?: (activityId: string, mode: TravelMode) => Promise<void>;
  readOnly?: boolean;
  isLoading?: boolean;
  error?: string | null;
}

const DayPlan: React.FC<DayPlanProps> = ({
  activities,
  days,
  selectedDay,
  tripTitle,
  tripStartDate,
  selectedActivityId,
  onAdd,
  onSelectDay,
  onSelectActivity,
  onEdit,
  onRemove,
  onChangeTravelMode,
  readOnly = false,
  isLoading = false,
  error = null,
}) => {
  const sortedActivities = activities
    .filter((activity) => activity.day === selectedDay)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  const daySpend = sortedActivities.reduce((total, activity) => total + parseBudget(activity.budget), 0);
  const dayHeading = selectedDay ? formatDayHeading(selectedDay, tripStartDate) : 'Day not set';

  // End-of-day leg: the drive from this day's last stop to the FIRST stop of the
  // next day that has any. That leg is stored on the next stop (its inbound
  // travelFromPrev), so we reuse it here, re-labelled "to <that stop>".
  const dayIndex = days.indexOf(selectedDay);
  const nextStop = dayIndex >= 0
    ? activities
        .filter((a) => days.indexOf(a.day) > dayIndex)
        .sort((a, b) => (days.indexOf(a.day) - days.indexOf(b.day)) || a.startTime.localeCompare(b.startTime))[0]
    : undefined;
  const outboundLeg: ActivityTravelLeg | null =
    sortedActivities.length > 0 && nextStop?.travelFromPrev
      ? { ...nextStop.travelFromPrev, crossDay: true, fromLabel: undefined, toLabel: nextStop.description }
      : null;

  if (!days.length) {
    return (
      <section className="v2-trip-day-card">
        <div className="v2-trip-day-card__empty">Add trip dates to start building your day plan.</div>
      </section>
    );
  }

  return (
    <section className="v2-trip-day-card">
      <header>
        <div>
          <p>{dayHeading}</p>
          <h2>{tripTitle}</h2>
        </div>
        <div className="v2-trip-day-card__spend">
          <span>Day spend</span>
          <strong>${daySpend}</strong>
        </div>
      </header>

      <div className="v2-trip-day-card__days" aria-label="Trip days">
        {days.map((day) => (
          <button
            type="button"
            key={day}
            className={day === selectedDay ? 'v2-trip-day-card__day--active' : undefined}
            onClick={() => onSelectDay(day)}
          >
            <span>{day.split(' ')[0]}</span>
            <strong>{day.split(' ')[1]}</strong>
          </button>
        ))}
      </div>

      <div className="v2-trip-day-card__summary">
        <p className="v2-trip-day-card__count">{sortedActivities.length} places scheduled</p>
        {!readOnly && <button type="button" onClick={onAdd}>+ Add activity</button>}
      </div>

      <div className="v2-trip-day-card__timeline">
        {isLoading && (
          <div className="v2-trip-day-card__empty">Loading activities...</div>
        )}
        {!isLoading && error && (
          <div className="v2-trip-day-card__empty">{error}</div>
        )}
        {!isLoading && !error && sortedActivities.map((activity, idx) => (
          <React.Fragment key={activity.id}>
            {/* idx 0 only has a leg when it's the advisory cross-day one (from
                the previous day); later stops always have their inbound leg. */}
            {activity.travelFromPrev && (
              <TravelLeg
                leg={activity.travelFromPrev}
                readOnly={readOnly}
                onChangeMode={onChangeTravelMode ? (mode) => onChangeTravelMode(activity.id, mode) : undefined}
              />
            )}
          <article
            className={`v2-trip-day-card__item ${activity.id === selectedActivityId ? 'v2-trip-day-card__item--selected' : ''}`}
            onClick={() => onSelectActivity(activity.id)}
          >
            <time>{activity.startTime}</time>
            <span className={`v2-trip-day-card__dot v2-trip-day-card__dot--${activity.category}`} />
            <div>
              <h3>{activity.description}</h3>
              <p>
                {activity.startTime}–{activity.endTime}
                {activity.place ? ` · ${activity.place}` : ''}
              </p>
            </div>
            <strong>{activity.budget || '—'}</strong>
            {!readOnly && (
              <div className="v2-trip-day-card__item-actions">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(activity);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(activity.id);
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </article>
          </React.Fragment>
        ))}
        {/* End-of-day leg: the onward drive to the next day's first stop. */}
        {!isLoading && !error && outboundLeg && nextStop && (
          <TravelLeg
            leg={outboundLeg}
            readOnly={readOnly}
            onChangeMode={onChangeTravelMode ? (mode) => onChangeTravelMode(nextStop.id, mode) : undefined}
          />
        )}
        {!isLoading && !error && sortedActivities.length === 0 && (
          <div className="v2-trip-day-card__empty">
            {readOnly ? 'No public activities for this day.' : 'No activities yet. Add one for this day.'}
          </div>
        )}
      </div>
    </section>
  );
};

interface ActivityEditorModalProps {
  draft: TripActivityDraft;
  editingActivityId: string | null;
  onDraftChange: (field: keyof TripActivityDraft, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const ActivityEditorModal: React.FC<ActivityEditorModalProps> = ({
  draft,
  editingActivityId,
  onDraftChange,
  onSave,
  onCancel,
}) => (
  <div className="v2-trip-activity-modal" role="dialog" aria-modal="true" aria-labelledby="activity-modal-title">
    <div className="v2-trip-activity-modal__backdrop" onClick={onCancel} />
    <section className="v2-trip-activity-modal__panel">
      <header>
        <div>
          <p>Day 3 · Oct 14</p>
          <h2 id="activity-modal-title">{editingActivityId ? 'Edit activity' : 'Add activity'}</h2>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close activity form">×</button>
      </header>

      <div className="v2-trip-day-card__editor">
        <label>
          Description
          <input
            type="text"
            value={draft.description}
            onChange={(event) => onDraftChange('description', event.target.value)}
            placeholder="e.g. Visit Pena Palace"
          />
        </label>
        <div className="v2-trip-day-card__editor-row">
          <label>
            Start
            <input
              type="time"
              value={draft.startTime}
              onChange={(event) => onDraftChange('startTime', event.target.value)}
            />
          </label>
          <label>
            End
            <input
              type="time"
              value={draft.endTime}
              onChange={(event) => onDraftChange('endTime', event.target.value)}
            />
          </label>
        </div>
        <label>
          Place <span>optional</span>
          <input
            type="text"
            value={draft.place}
            onChange={(event) => onDraftChange('place', event.target.value)}
            placeholder="Location or meeting point"
          />
        </label>
        <div className="v2-trip-day-card__editor-row">
          <label>
            Budget <span>optional</span>
            <input
              type="text"
              value={draft.budget}
              onChange={(event) => onDraftChange('budget', event.target.value)}
              placeholder="$0"
            />
          </label>
          <label>
            Type
            <select
              value={draft.category}
              onChange={(event) => onDraftChange('category', event.target.value as TripActivityDraft['category'])}
            >
              <option value="activity">Activity</option>
              <option value="travel">Travel</option>
              <option value="food">Food</option>
            </select>
          </label>
        </div>
        <div className="v2-trip-day-card__editor-actions">
          {editingActivityId && (
            <button type="button" className="v2-trip-day-card__editor-cancel" onClick={onCancel}>
              Cancel
            </button>
        )}
          <button type="button" onClick={onSave}>
            {editingActivityId ? 'Save changes' : 'Add activity'}
          </button>
        </div>
      </div>
    </section>
  </div>
);

function createEmptyActivityDraft(): TripActivityDraft {
  return {
    description: '',
    startTime: '',
    endTime: '',
    place: '',
    budget: '',
    category: 'activity',
  };
}

function parseBudget(budget?: string): number {
  if (!budget) return 0;
  const value = Number(budget.replace(/[^0-9.]/g, ''));
  return Number.isFinite(value) ? value : 0;
}

function parseActivityBudget(budget?: string): CreateActivityRequest['budget'] {
  if (!budget) return undefined;

  const value = Number(budget.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(value) || value <= 0) return undefined;

  return {
    amount: value,
    currency: budget.includes('€') ? 'EUR' : budget.includes('£') ? 'GBP' : 'USD',
  };
}

export default TripDetailPage;
